import { useEffect, useRef, useState, useMemo } from 'react'
import {
  createChart, CrosshairMode, CandlestickSeries, createSeriesMarkers,
  type IChartApi, type ISeriesApi,
} from 'lightweight-charts'
import { useLanguage } from '@/i18n/LanguageContext'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Users, TrendingUp, TrendingDown, Plus, ArrowLeft, Clock } from 'lucide-react'
import { toast } from 'sonner'
import {
  useFollowerCandles, usePageEvents, useCreateAnnotation, useDeleteAnnotation,
  type FinancePage,
} from '@/api/hooks/usePages'

const fmtNum = new Intl.NumberFormat('pt-BR')
const TIMEFRAMES = ['2h', '4h', '8h', '12h', '24h'] as const
type TF = typeof TIMEFRAMES[number]

const TF_RANGE_MS: Record<TF, number> = {
  '2h':  7 * 24 * 60 * 60 * 1000,   // 7 dias (~84 candles)
  '4h':  14 * 24 * 60 * 60 * 1000,  // 14 dias (~84 candles)
  '8h':  30 * 24 * 60 * 60 * 1000,  // 30 dias (~90 candles)
  '12h': 45 * 24 * 60 * 60 * 1000,  // 45 dias (~90 candles)
  '24h': 90 * 24 * 60 * 60 * 1000,  // 90 dias (~90 candles)
}

interface Props { pages: FinancePage[]; initialPageId?: string; onBack?: () => void }

export function FollowerCandleChart({ pages, initialPageId, onBack }: Props) {
  const { t } = useLanguage()
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef   = useRef<IChartApi | null>(null)
  const seriesRef  = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const markersRef = useRef<ReturnType<typeof createSeriesMarkers> | null>(null)

  const [selectedPageId, setSelectedPageId] = useState<string | null>(
    () => initialPageId ?? pages.find(p => p.fbPageId)?.id ?? null,
  )
  const [tf, setTf] = useState<TF>('4h')
  const [annotationOpen, setAnnotationOpen] = useState(false)
  const [annotationLabel, setAnnotationLabel] = useState('')
  const [annotationDate, setAnnotationDate] = useState('')
  const [countdown, setCountdown] = useState('')

  useEffect(() => {
    const tick = () => {
      const diff = Math.ceil(Date.now() / 7200000) * 7200000 - Date.now()
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setCountdown(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // Estabilizar from/to arredondando para 5 min — evita queryKey mudar a cada render do countdown
  const [from, to] = useMemo(() => {
    const fiveMin = 5 * 60 * 1000
    const n = Math.floor(Date.now() / fiveMin) * fiveMin
    return [n - TF_RANGE_MS[tf], n]
  }, [tf])

  const { data: candles = [] } = useFollowerCandles(selectedPageId, tf, from, to)
  const { data: events = [] }  = usePageEvents(selectedPageId, from, to)
  const createAnn = useCreateAnnotation()
  // kept in scope for future use (delete button on event markers)
  useDeleteAnnotation()

  const stats = useMemo(() => {
    if (candles.length === 0) return null
    const last  = candles[candles.length - 1]
    const first = candles[0]
    const delta24h = candles.length >= 6
      ? last.close - candles[Math.max(0, candles.length - 6)].open
      : last.close - first.open
    const delta7d = last.close - first.open
    const bestCandle  = Math.max(...candles.map(c => c.close - c.open))
    const worstCandle = Math.min(...candles.map(c => c.close - c.open))
    return { current: last.close, delta24h, delta7d, bestCandle, worstCandle }
  }, [candles])

  // Init chart
  useEffect(() => {
    if (!chartContainerRef.current) return
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#0d0f16' },
        textColor: '#94a3b8',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1a1d2a' },
        horzLines: { color: '#1a1d2a' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#252836' },
      timeScale: { borderColor: '#252836', timeVisible: true },
      width: chartContainerRef.current.clientWidth,
      height: 360,
    })

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    })

    const markers = createSeriesMarkers(series, [])

    chartRef.current   = chart
    seriesRef.current  = series
    markersRef.current = markers

    const ro = new ResizeObserver(() => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth })
      }
    })
    ro.observe(chartContainerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current   = null
      seriesRef.current  = null
      markersRef.current = null
    }
  }, [])

  // Update candles
  useEffect(() => {
    if (!seriesRef.current || !chartRef.current || candles.length === 0) return
    // Doji fix: quando open==close (1 snapshot por bucket, ex: 2h), usar prevClose como open
    // para que o corpo do candle seja proporcional à variação real entre snapshots
    const data = candles.map((c, i) => {
      const isDoji = c.open === c.close
      const prevClose = i > 0 ? candles[i - 1].close : null
      const open = isDoji && prevClose !== null ? prevClose : c.open
      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        time: c.time as any,
        open,
        high: Math.max(open, c.close),
        low:  Math.min(open, c.close),
        close: c.close,
      }
    })
    seriesRef.current.setData(data)
    chartRef.current.timeScale().fitContent()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((chartRef.current.timeScale().options() as any).barSpacing > 16) {
      chartRef.current.timeScale().applyOptions({ barSpacing: 16 })
    }
  }, [candles])

  // Update markers
  useEffect(() => {
    if (!markersRef.current) return
    const markerData = events.map(ev => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      time: ev.time as any,
      position: 'belowBar' as const,
      color: ev.type === 'post' ? '#c9a84c' : '#64748b',
      shape: 'arrowUp' as const,
      text: ev.type === 'post' ? 'P' : 'N',
      size: 1,
    }))
    markersRef.current.setMarkers(markerData.sort((a, b) => a.time - b.time))
  }, [events])

  const handleSaveAnnotation = () => {
    if (!selectedPageId || !annotationDate) return
    const event_at = new Date(annotationDate).getTime()
    if (isNaN(event_at)) { toast.error('Data inválida'); return }
    createAnn.mutate(
      { pageId: selectedPageId, label: annotationLabel, event_at },
      {
        onSuccess: () => {
          toast.success('Anotação salva')
          setAnnotationOpen(false)
          setAnnotationLabel('')
          setAnnotationDate('')
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const pagesWithFb = pages.filter(p => p.fbPageId)

  if (pagesWithFb.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
        <Users className="h-10 w-10 opacity-30" />
        <p className="text-sm">{t('Nenhuma pagina com FB Page ID configurado')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Top controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {onBack && (
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 text-xs px-2" onClick={onBack}>
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar
          </Button>
        )}
        <select
          value={selectedPageId ?? ''}
          onChange={e => setSelectedPageId(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {pagesWithFb.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <span className="text-xs text-primary font-semibold uppercase tracking-wide hidden sm:block">
          Análise de Padrões de Crescimento
        </span>

        {countdown && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto mr-1 font-mono">
            <Clock className="h-3 w-3" />
            <span>{countdown}</span>
          </div>
        )}

        <div className="flex gap-1 rounded-lg bg-muted p-0.5">
          {TIMEFRAMES.map(tfOpt => (
            <button
              key={tfOpt}
              onClick={() => setTf(tfOpt)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                tf === tfOpt
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tfOpt}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
          <Card className="p-3 col-span-2 sm:col-span-1 border-primary/20 bg-primary/5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Seguidores</div>
            <div className="text-xl font-bold text-primary">{fmtNum.format(stats.current)}</div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">24h</div>
            <div className={`text-lg font-bold ${stats.delta24h >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {stats.delta24h >= 0 ? '+' : ''}{fmtNum.format(stats.delta24h)}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Período</div>
            <div className={`text-lg font-bold ${stats.delta7d >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {stats.delta7d >= 0 ? '+' : ''}{fmtNum.format(stats.delta7d)}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-emerald-500" /> Melhor candle
            </div>
            <div className="text-lg font-bold text-emerald-500">
              +{fmtNum.format(Math.max(0, stats.bestCandle))}
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <TrendingDown className="h-3 w-3 text-red-500" /> Pior candle
            </div>
            <div className="text-lg font-bold text-red-500">
              {fmtNum.format(Math.min(0, stats.worstCandle))}
            </div>
          </Card>
        </div>
      )}

      {/* Chart */}
      <Card className="overflow-hidden border-border/60 relative">
        <div ref={chartContainerRef} style={{ height: 360 }} />
        {candles.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground bg-background/80">
            <Users className="h-8 w-8 opacity-20" />
            <p className="text-sm">{t('Aguardando snapshots...')}</p>
            <p className="text-xs opacity-60">{t('O grafico aparece apos a proxima coleta (aprox. 2h)')}</p>
          </div>
        )}
      </Card>

      {/* Events legend */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full bg-primary" /> Post publicado (P)
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-500" /> Anotação manual (N)
        </div>
        <Button
          variant="outline" size="sm" className="ml-auto gap-1.5 h-7 text-xs"
          onClick={() => setAnnotationOpen(true)}
        >
          <Plus className="h-3 w-3" /> Anotar evento
        </Button>
      </div>

      {/* Annotation modal */}
      <Dialog open={annotationOpen} onOpenChange={setAnnotationOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('Nova Anotacao')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>{t('Descricao')}</Label>
              <Input
                className="mt-1"
                placeholder="Ex: Viral no TikTok, pico de engajamento..."
                maxLength={140}
                value={annotationLabel}
                onChange={e => setAnnotationLabel(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('Data e hora do evento')}</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                value={annotationDate}
                onChange={e => setAnnotationDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnotationOpen(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleSaveAnnotation} disabled={createAnn.isPending}>
              {createAnn.isPending ? t('Salvando...') : t('Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
